import { useCallback, useEffect, useMemo, useRef } from "react";

export interface IDebouncedCommit<T> {
  /** Remembers `value` and (re)starts the timer; only the last value of a burst is committed. */
  schedule: (value: T) => void;
  /** Commits a pending value now (blur, Enter, unmount). No-op when nothing is pending. */
  flush: () => void;
  /** Drops a pending value without committing it. */
  cancel: () => void;
  /** Whether a value is waiting for the timer. */
  isPending: () => boolean;
}

interface IPending<T> {
  value: T;
  /** The commit as it was when the value was scheduled — it closes over the component edited then. */
  commit: (value: T) => void;
}

/**
 * Coalesces a burst of edits (typing, repeated stepper clicks) into ONE commit — one undo step, one
 * collab op. The commit function is captured WITH the value at schedule time: when the selection
 * moves on mid-burst and the control re-renders for another component (same control, new props), the
 * pending edit still lands on the component it was made for. A pending value is also flushed on
 * unmount.
 */
export function useDebouncedCommit<T>(commit: (value: T) => void, delayMs: number): IDebouncedCommit<T> {
  const commitRef: React.MutableRefObject<(value: T) => void> = useRef(commit);
  commitRef.current = commit;
  const pendingRef: React.MutableRefObject<IPending<T> | null> = useRef<IPending<T> | null>(null);
  const timerRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null> = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  const clearTimer = useCallback((): void => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const flush = useCallback((): void => {
    clearTimer();
    const pending: IPending<T> | null = pendingRef.current;
    pendingRef.current = null;
    if (pending !== null) {
      pending.commit(pending.value);
    }
  }, [clearTimer]);

  const schedule = useCallback(
    (value: T): void => {
      pendingRef.current = { value, commit: commitRef.current };
      clearTimer();
      timerRef.current = setTimeout(flush, delayMs);
    },
    [clearTimer, flush, delayMs]
  );

  const cancel = useCallback((): void => {
    clearTimer();
    pendingRef.current = null;
  }, [clearTimer]);

  const isPending = useCallback((): boolean => pendingRef.current !== null, []);

  useEffect((): (() => void) => flush, [flush]);

  return useMemo(
    (): IDebouncedCommit<T> => ({ schedule, flush, cancel, isPending }),
    [schedule, flush, cancel, isPending]
  );
}
